import { all, put, call, debounce, select, takeLatest, take, race } from 'redux-saga/effects'
import { mergeWith, union } from 'lodash'

import * as api from 'Service/Api'

import selectors from 'Redux/Selectors'
import * as types from 'Redux/Types/LedgerTypes'
import * as actions from 'Redux/Actions/LedgerActions'

import { mergeUpdates } from 'Utils'

import moment from 'moment'

import { DATE_FIELDS } from 'Constants'

const attachmentTypeMap = {
  PURCHASE_ORDERS: 'purchaseOrders',
  SHIPPING_DOCS: 'shippingDocuments',
  OTHER_DOCS: 'otherAttachments',
}

const downloadFile = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.onreadystatechange = () => {
      const { readyState, response, status } = xhr
      if (readyState === 4) {
        if (status === 200) {
          resolve(response)
        } else {
          reject(xhr)
        }
      }
    }
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.send()
  })
}

const convertFileToB64 = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.onload = (event) => {
      const base64 = event.target.result
      resolve(base64)
    }
    fileReader.onerror = (error) => {
      reject(error)
    }

    fileReader.readAsDataURL(file)
  })
}

const removeBase64 = (array) => {
  return array?.map(({ base64, ...item }) => {
    return item
  })
}

const mergeExpenseUpdate = (payload, ledger) => {
  const { documentType, merchant, transactionDate, createdDate, ...orders } = payload
  const { label, ...doc } = documentType
  const { purchaseOrders = [], shippingDocuments = [], otherAttachments = [] } = orders
  const strippedOrders = {
    ...orders,
    purchaseOrders: removeBase64(purchaseOrders),
    shippingDocuments: removeBase64(shippingDocuments),
    otherAttachments: removeBase64(otherAttachments),
  }
  const mergedOrders = mergeWith({ ...ledger.selectedDocOrders }, strippedOrders, mergeUpdates)

  return mergeWith(
    { ...ledger.selectedDocument, ...ledger.selectedDocMappings },
    { documentType: doc, merchant, transactionDate, createdDate, orders: mergedOrders },
    mergeUpdates,
  )
}

function* attachmentURLsToB64(expenseObject) {
  try {
    const { orders } = expenseObject
    const [{ purchaseOrders, shippingDocuments, otherAttachments }] = orders

    if (purchaseOrders) {
      for (let i = 0; i < purchaseOrders.length; ++i) {
        try {
          const { url } = purchaseOrders[i]
          const file = yield call(downloadFile, url)
          const base64 = yield call(convertFileToB64, file)
          purchaseOrders[i].base64 = base64
        } catch (error) {
          //no-op
        }
      }
    }
    if (shippingDocuments) {
      for (let i = 0; i < shippingDocuments.length; ++i) {
        try {
          const { url } = shippingDocuments[i]
          const file = yield call(downloadFile, url)
          const base64 = yield call(convertFileToB64, file)
          shippingDocuments[i].base64 = base64
        } catch (error) {
          //no-op
        }
      }
    }
    if (otherAttachments) {
      for (let i = 0; i < otherAttachments.length; ++i) {
        try {
          const { url } = otherAttachments[i]
          const file = yield call(downloadFile, url)
          const base64 = yield call(convertFileToB64, file)
          otherAttachments[i].base64 = base64
        } catch (error) {
          //no-op
        }
      }
    }
    return expenseObject
  } catch (error) {
    console.log(error)
  }
}

function* deleteExpenseWorker({ payload }) {
  const { filters, guid, limit, offset } = payload

  try {
    yield call(api.deleteLedgerRecord, guid)
    yield put(actions.fetchExpenses({ ...filters, limit, offset }))

    const [, error] = yield race([
      take(types.FETCH_EXPENSES_SUCCEEDED),
      take(types.FETCH_EXPENSES_FAILED),
    ])

    if (error) {
      throw error
    }

    yield put(actions.deleteExpenseSucceeded(payload))
  } catch (errors) {
    yield put(actions.deleteExpenseFailed(errors))
  }
}

function* fetchExpenseWorker({ payload }) {
  const { guid } = payload
  try {
    const [{ data }] = yield all([call(api.getLedgerRecordByGuid, guid)])
    const expenseObject = yield call(attachmentURLsToB64, data)
    yield put(actions.fetchExpenseSucceeded(expenseObject))
  } catch (error) {
    yield put(actions.fetchExpenseFailed(error))
  }
}

function* fetchExpensesWorker({ payload }) {
  try {
    const response = yield call(api.getLedgerRecords, payload)
    yield put(actions.fetchExpensesSucceeded(response))
  } catch (errors) {
    yield put(actions.fetchExpensesFailed(errors))
  }
}

/* eslint-disable */
const mapValueToAttributes = ({ attributes, updatedAttributes }) => {
  updatedAttributes.map(async ({ attributeName, attributeValue }) => {
    const attIdx = attributes.findIndex((att) => att.type.name === attributeName)
    if (attIdx > -1) {
      attributes[attIdx].value = attributeValue || ''
    } else {
      await attributes.push({
        isDateField: false,
        isFinancialField: false,
        type: {
          name: attributeName,
        },
        value: attributeValue || '',
        createdDate: null,
      })
    }
  })
  return attributes
}
function* updateExpenseWorker({ payload }) {
  try {
    const { ledger } = yield select()
    const mergedDoc = mergeExpenseUpdate(payload, ledger)
    mergedDoc.orders[0].attributes = updateMergedDocsDateFields(mergedDoc.orders[0]?.attributes)
    mergedDoc.orders[0].orderNumber = payload?.orderNumber || ''
    mergedDoc.transactionDate = getFormattedDate(mergedDoc.transactionDate)
    mergedDoc.createdDate = getFormattedDate(mergedDoc.createdDate)
    const { data } = yield call(api.updateExpense, ledger.selectedDocument.guid, mergedDoc)
    const expenseObject = yield call(attachmentURLsToB64, data[0])
    yield put(actions.updateExpenseSuccess(expenseObject))
  } catch (error) {
    console.log({ error })
    yield put(actions.updateExpenseFailed(error))
  }
}
const getFormattedDate = (rawDate) =>
  moment(rawDate).isValid() ? moment(rawDate).utc().format('YYYY-MM-DD HH:mm:ss') : null
const updateMergedDocsDateFields = (attributes = []) => {
  return attributes.map((fieldData = {}) => {
    if (DATE_FIELDS.indexOf(fieldData.type?.name) >= 0) {
      const fieldValue = moment(fieldData.value).isValid()
        ? moment(fieldData.value).utc().format('YYYY-MM-DD HH:mm:ss')
        : null
      return { ...fieldData, value: fieldValue }
    }
    return { ...fieldData }
  })
}

function* attachDocumentsWorker({ payload }) {
  const expenseDocument = yield select(selectors.getSelectedDocument)
  try {
    const { ledger } = yield select()
    const urls = payload.attachments?.map(({ file, meta }) => ({
      filename: file.name,
      url: meta.url.split('?')[0],
    }))

    const type = attachmentTypeMap[payload.type]
    const mergedDoc = mergeExpenseUpdate(
      { ...expenseDocument, [type]: union(expenseDocument[type], urls) },
      ledger,
    )

    mergedDoc.orders[0].attributes = updateMergedDocsDateFields(mergedDoc.orders[0]?.attributes)

    const { data } = yield call(api.updateExpense, ledger.selectedDocument.guid, mergedDoc)

    const expenseObject = yield call(attachmentURLsToB64, data[0])

    yield put(actions.attachDocumentsSuccess({ data: expenseObject, type: payload.type }))
  } catch (error) {
    yield put(actions.attachDocumentsFailed(payload.type))
  }
}

function* deleteAttachmentWorker({ payload }) {
  const expenseDocument = yield select(selectors.getSelectedDocument)
  try {
    const { ledger } = yield select()
    const type = attachmentTypeMap[payload.type]

    const mergedDoc = mergeExpenseUpdate(
      {
        ...expenseDocument,
        [type]: expenseDocument[type]?.filter(({ url }) => {
          return url !== payload.url
        }),
      },
      ledger,
    )

    const { data } = yield call(api.updateExpense, ledger.selectedDocument.guid, mergedDoc)
    const expenseObject = yield call(attachmentURLsToB64, data[0])
    yield put(actions.deleteAttachmentSucceeded({ data: expenseObject, type: payload.type }))
  } catch (error) {
    yield put(actions.deleteAttachmentFailed(payload.type))
  }
}

function* fetchDocsWorkflowSearch({ payload }) {
  try {
    const response = yield call(api.getDocsWorkflowSearch, payload)
    yield put(actions.fetchDocsWorkflowSearchSucceeded(response))
  } catch (errors) {
    yield put(actions.fetchDocsWorkflowSearchFailed(errors))
  }
}

export default function* ledgerSaga() {
  yield takeLatest(types.DELETE_EXPENSE, deleteExpenseWorker)
  yield debounce(150, types.FETCH_EXPENSE, fetchExpenseWorker)
  yield debounce(150, types.FETCH_EXPENSES, fetchExpensesWorker)
  yield takeLatest(types.UPDATE_EXPENSE, updateExpenseWorker)
  yield takeLatest(types.ATTACH_DOCUMENTS, attachDocumentsWorker)
  yield takeLatest(types.DELETE_ATTACHMENT, deleteAttachmentWorker)
  yield debounce(150, types.FETCH_DOCS_WORKFLOW_SEARCH, fetchDocsWorkflowSearch)
}
